/**
 * GA4 Analytics module
 * - Single initialization with REACT_APP_GA_TRACKING_ID from .env
 * - Page view tracking via react-router
 * - trackCustomerJourneyEvent with auto user_id / tracking_id
 * - GTM dataLayer compatibility (event key)
 * - GA4 best practices: lowercase event names, structured params
 */

import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_TRACKING_ID || "";
const SECOND_GA_MEASUREMENT_ID =process.env.REACT_APP_GA_GOOGLE_PIXEL_ID || "";

function getMeasurementIds(): string[] {
  const ids = [GA_MEASUREMENT_ID, SECOND_GA_MEASUREMENT_ID].filter(
    (id): id is string => Boolean(id && id.trim())
  );

  return Array.from(new Set(ids));
}

/** GA4 event parameter values (recommended: string, number, boolean) */
export type GAEventParamValue = string | number | boolean | undefined;

/** Structured params for GA4 events */
export type GAEventParams = Record<string, GAEventParamValue>;

/** Context set once (e.g. after login) and auto-attached to events */
interface AnalyticsContext {
  user_id?: string;
  tracking_id?: string;
}

let isInitialized = false;
const context: AnalyticsContext = {};

/**
 * Initialize GA4 once. Safe to call multiple times; only the first call runs.
 */
export function initAnalytics(): void {
  if (isInitialized) return;

  const measurementIds = getMeasurementIds();

  if (!measurementIds.length) {
    console.warn(
      "Google Analytics: REACT_APP_GA_TRACKING_ID and REACT_APP_GA_TRACKING_ID_2 are not set in .env"
    );
    return;
  }

  ReactGA.initialize(
    measurementIds.map((trackingId) => ({ trackingId })) as any,
    {
      // testMode: false,
      gtagOptions: { send_page_view: false },
      // gaOptions: {
      //           debug_mode: true,
      //       },
      //       gtagOptions: {
      //           debug_mode: true,
      //           send_page_view: false,
      //       },
      }
  );

  isInitialized = true;
}

/**
 * Set user/tracking context so they are automatically attached to events.
 * Call after login or when tracking_id becomes available.
 */
export function setAnalyticsContext(updates: {
  user_id?: string;
  tracking_id?: string;
}): void {
  if (updates.user_id !== undefined) context.user_id = updates.user_id;
  if (updates.tracking_id !== undefined) context.tracking_id = updates.tracking_id;
}

/**
 * Normalize event name for GA4 (lowercase, consistent).
 */
function normalizeEventName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * Hook to track page views on route change (use inside Router).
 */
export function usePageTracking(): void {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === previousPathRef.current) return;
    previousPathRef.current = path;

    if (!isInitialized) return;

    ReactGA.send({
      hitType: "pageview",
      page: path,
      title: document.title || path,
    });

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "page_view",
        page_path: path,
        page_title: document.title || path,
      });
    }
  }, [location.pathname, location.search]);
}

/*
  Example usage – button click:

  import { trackCustomerJourneyEvent, setAnalyticsContext } from "../utils/analytics";

  function MyComponent() {
    const handleApplyClick = () => {
      trackCustomerJourneyEvent("apply_now_clicked", {
        button_location: "hero",
        campaign: "homepage",
      });
    };

    const handleStartEligibility = () => {
      setAnalyticsContext({ tracking_id: eligibilityTrackingId });
      trackCustomerJourneyEvent("eligibility_started", {
        source: "dashboard",
      });
    };

    return (
      <button type="button" onClick={handleApplyClick}>
        Apply now
      </button>
    );
  }
*/
